import { useEffect, useRef } from 'react';
import confirmDialog from '../../../components/ConfirmDialog';

/** Tom's answer 1 on #871: autosave every 15 minutes while there are unsaved changes. */
export const AUTOSAVE_MS = 15 * 60 * 1000;

/**
 * Save `interval` after the first unsaved change, and again every `interval`
 * while changes stay unsaved; nothing runs while the draft is saved.
 */
export default function useAutosave({ dirty, save, interval = AUTOSAVE_MS }) {
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!dirty) return undefined;
    const timer = setInterval(() => { saveRef.current(); }, interval);
    return () => clearInterval(timer);
  }, [dirty, interval]);
}

export const LEAVE_QUESTION = 'The sheet has unsaved changes. Leave and lose them?';

/** A click that follows an in-app link in this tab: plain left click, same origin, no download or other target. */
function inAppLink(e) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
  if (!a || a.hasAttribute('download') || (a.target && a.target !== '_self')) return null;
  let url;
  try { url = new URL(a.getAttribute('href'), window.location.href); } catch (err) { return null; }
  if (url.origin !== window.location.origin) return null;
  // A link to this very page (a hash, or the same address) leaves nothing.
  if (url.pathname === window.location.pathname && url.search === window.location.search) return null;
  return a;
}

/**
 * The one popstate listener the leave guard owns. It has to run before React
 * Router's, and neither a later registration nor the capture phase gets it
 * there: popstate fires at the window itself, and Chromium calls a target's
 * listeners in the order they were added, capture or not (#926). A guard added
 * when the builder mounts came after the router's (BrowserRouter listens from
 * the first render), so the router had already moved to the page behind by
 * the time the author pressed Cancel, and the step back remounted the builder
 * from the saved sheet. So index.js installs this before the router renders,
 * and the page hands it the check to make while it has unsaved changes.
 */
let popCheck = null;
let popInstalled = false;
export function installLeaveGuard() {
  if (popInstalled || typeof window === 'undefined') return;
  popInstalled = true;
  window.addEventListener('popstate', (e) => { if (popCheck) popCheck(e); }, true);
}

/**
 * Leaving with unsaved changes asks first (Tom's answer 1 on #871): the
 * browser's own prompt on a reload or a closed tab, and the in-page confirm
 * (components/ConfirmDialog) on the ways out inside the app. The app runs on a
 * plain BrowserRouter, which has no navigation blocker, so the guard sits in
 * front of it: a click on an in-app link is caught on the way down (window,
 * capture phase) before the router's Link sees it, and the browser's Back or
 * Forward is caught by the popstate listener installLeaveGuard put ahead of
 * the router's, then undone with history.go when the author stays.
 *
 * The question is answered after the event is over, so both ways out are held
 * first and replayed on a yes: the link is clicked again, and the popstate is
 * raised again for the router (the address is already where the author went).
 */
export function useLeavePrompt(dirty, question = LEAVE_QUESTION) {
  useEffect(() => {
    if (!dirty) return undefined;
    installLeaveGuard();
    const onLeave = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    let passClick = null;   // the link a yes is clicking again
    let passPop = false;    // the popstate a yes is raising again
    const onClick = (e) => {
      const a = inAppLink(e);
      if (!a) return;
      if (passClick === a) { passClick = null; return; }
      e.preventDefault();
      e.stopPropagation();
      confirmDialog(question, { confirmLabel: 'Leave' }).then((yes) => {
        if (!yes) return;
        passClick = a;
        a.click();
      });
    };
    // The router keeps its place in history as state.idx.
    const idxOf = (state) => (state && Number.isInteger(state.idx) ? state.idx : null);
    const here = idxOf(window.history.state);
    let returning = false;
    let pending = null;     // the popstate the open question is about, latest first
    const onPop = (e) => {
      if (returning) {
        // The step back to the builder after a Cancel: the router never left.
        returning = false;
        e.stopImmediatePropagation();
        return;
      }
      if (passPop) { passPop = false; return; }
      e.stopImmediatePropagation();
      // A further Back or Forward while the question is open moves the
      // address again but asks nothing more: the one answer acts on where
      // the author now is.
      const already = pending;
      pending = { state: e.state };
      if (already) return;
      confirmDialog(question, { confirmLabel: 'Leave' }).then((yes) => {
        const { state } = pending;
        pending = null;
        if (yes) {
          passPop = true;
          window.dispatchEvent(new PopStateEvent('popstate', { state }));
          return;
        }
        const to = idxOf(state);
        const delta = here !== null && to !== null ? here - to : 1;
        if (delta) { returning = true; window.history.go(delta); }
      });
    };
    window.addEventListener('beforeunload', onLeave);
    window.addEventListener('click', onClick, true);
    popCheck = onPop;
    return () => {
      window.removeEventListener('beforeunload', onLeave);
      window.removeEventListener('click', onClick, true);
      if (popCheck === onPop) popCheck = null;
    };
  }, [dirty, question]);
}
